import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../../shared/components/Button';
import { OnboardingShell } from '../../features/onboarding/components/OnboardingShell';
import { SignaturePad } from '../../features/onboarding/components/SignaturePad';
import {
  useSelfOnboardContract,
  useSignSelfOnboardContract,
} from '../../features/onboarding/hooks/useSelfOnboardContract';
import { ContractAcceptanceMethod } from '../../features/onboarding/types';
import { buildSignPayload, canSubmitSigning } from '../../features/onboarding/signing';

/**
 * PE-2097 — merchant self-onboarding: contract review & signing.
 *
 * Reached publicly (no session) via the single-use link emailed at sign-up:
 *   {adminUrl}/onboard/contract?merchantId=<id>&token=<token>
 * The applicant reviews the assigned contract template and completes the signing
 * flow (explicit accept OR drawn e-signature) before the application can proceed
 * to PENDING_APPROVAL. Endpoints are @Public on watchman, reached through the
 * osprey-api iam-proxy — see features/onboarding/api/selfOnboardApi.ts.
 */
export const SignContractPage = () => {
  const [params] = useSearchParams();
  const merchantId = params.get('merchantId');
  const token = params.get('token');

  const { data: contract, isLoading, isError, error } = useSelfOnboardContract(merchantId, token);
  const sign = useSignSelfOnboardContract(merchantId);

  const [method, setMethod] = useState<ContractAcceptanceMethod>(ContractAcceptanceMethod.EXPLICIT_ACCEPT);
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [formError, setFormError] = useState('');

  const canSubmit = canSubmitSigning({
    hasContract: !!contract,
    method,
    accepted,
    hasSignature: !!signature,
    submitting: sign.isPending,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!token || !canSubmit) return;
    sign.mutate(buildSignPayload(token, method, signature), {
      onError: (err: unknown) => setFormError((err as { message?: string })?.message || 'Could not sign the contract'),
    });
  };

  // Missing/expired link → nothing to sign.
  if (!merchantId || !token) {
    return (
      <OnboardingShell>
        <div className="w-full rounded-2xl border border-border bg-card text-card-foreground shadow-sm px-6 py-10 text-center">
          <h1 className="text-[22px] font-bold mb-2">Invalid contract link</h1>
          <p className="text-sm text-muted-foreground">
            This link is missing information. Please use the most recent link from your sign-up email.
          </p>
        </div>
      </OnboardingShell>
    );
  }

  // Signed → pending-approval confirmation.
  if (sign.isSuccess) {
    return (
      <OnboardingShell>
        <div className="w-full rounded-2xl border border-border bg-card text-card-foreground shadow-sm px-6 py-10 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="text-[22px] font-bold mb-2">Contract signed</h1>
          <p className="text-sm text-muted-foreground">
            {sign.data?.message || 'Your application is now awaiting review. We’ll email you once it’s approved.'}
          </p>
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell>
      <div className="w-full rounded-2xl border border-border bg-card text-card-foreground shadow-sm px-6 py-8 sm:px-8">
        <div className="mb-6">
          <h1 className="text-[24px] font-bold mb-1">Review &amp; sign your contract</h1>
          <p className="text-sm text-muted-foreground">
            Please read the agreement below and complete signing to continue your application.
          </p>
        </div>

        {isLoading && (
          <div className="py-16 text-center text-sm text-muted-foreground">Loading your contract…</div>
        )}

        {isError && (
          <div className="rounded-[12px] border border-red-200 bg-red-50 px-4 py-6 text-center">
            <p className="text-sm font-[600] text-red-700">
              {(error as { message?: string })?.message || 'Contract link is invalid or has expired'}
            </p>
            <p className="mt-1 text-[13px] text-red-600/80">
              Please use the most recent link from your sign-up email, or contact support.
            </p>
          </div>
        )}

        {contract && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-[13px] font-[700] text-foreground">{contract.templateName}</span>
                <span className="text-[12px] text-muted-foreground">Version {contract.version}</span>
              </div>
              {/* Fully-sandboxed iframe (no scripts/forms/same-origin) so the contract HTML is inert
                  and isolated from osprey's origin & session — same approach as the admin template preview. */}
              <iframe
                title="Contract"
                sandbox=""
                srcDoc={contract.contentHtml}
                className="w-full min-h-[360px] rounded-[12px] border border-border bg-white"
              />
            </div>

            <fieldset className="space-y-3">
              <legend className="text-[13px] font-[700] text-foreground mb-1">How would you like to sign?</legend>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name="method"
                  className="mt-1"
                  checked={method === ContractAcceptanceMethod.EXPLICIT_ACCEPT}
                  onChange={() => setMethod(ContractAcceptanceMethod.EXPLICIT_ACCEPT)}
                />
                <span className="text-sm text-foreground">Confirm &amp; accept</span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name="method"
                  className="mt-1"
                  checked={method === ContractAcceptanceMethod.E_SIGNATURE}
                  onChange={() => setMethod(ContractAcceptanceMethod.E_SIGNATURE)}
                />
                <span className="text-sm text-foreground">Draw my signature</span>
              </label>
            </fieldset>

            {method === ContractAcceptanceMethod.EXPLICIT_ACCEPT ? (
              <label className="flex items-start gap-2.5 cursor-pointer rounded-[12px] border border-border bg-muted/40 px-4 py-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <span className="text-[13px] text-foreground">
                  I have read and agree to the terms of <strong>{contract.templateName}</strong> (version {contract.version}).
                </span>
              </label>
            ) : (
              <SignaturePad onChange={setSignature} disabled={sign.isPending} />
            )}

            {formError && <p className="text-[13px] font-[600] text-red-500">{formError}</p>}

            <Button type="submit" disabled={!canSubmit} loading={sign.isPending} className="w-full">
              Sign contract
            </Button>
          </form>
        )}
      </div>
    </OnboardingShell>
  );
};
